/**
 * Per-device preferences: language, sound and the name you play under. Stored locally and never sent to the
 * server, so two people at one table can play in different languages. scope.md §10.
 */

import { DEFAULT_LOCALE, type Locale, isLocale } from '@garbage/shared';

const LOCALE_KEY = 'garbage.locale';
const SOUND_KEY = 'garbage.sound';
const NAME_KEY = 'garbage.name';

export function loadLocale(): Locale {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    return isLocale(saved) ? saved : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function saveLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* ignore */
  }
}

/** Sounds are off until somebody turns them on. Ben's call, 17 September 2026. */
export function loadSound(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) === 'on';
  } catch {
    return false;
  }
}

export function saveSound(on: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    /* ignore */
  }
}

/**
 * The name last played under on this device, so it is filled in next time and can be
 * changed. Kept only here: the server holds it for the life of a room and game records
 * never have it. Ben, 1 October 2026.
 */
export function loadName(): string {
  try {
    return (localStorage.getItem(NAME_KEY) ?? '').slice(0, 16);
  } catch {
    return '';
  }
}

export function saveName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    /* ignore */
  }
}
